/**
 * System achievements — the platform's own badges, as opposed to the ones a collection awards.
 *
 * These had no screen at all until now: they were created by calling the Admin API directly, or by
 * SQL. The seeder that defines them only runs against a database that does not yet exist, so it
 * never reaches production — which is how production came to have none.
 */

/**
 * Mirrors the server's `SystemAchievementCriteriaType`.
 *
 * Ordinals on the wire: this API has no global string-enum converter, so these numbers *are* the
 * contract. Grouped in tens on the server — 1-9 completion, 10-14 exploration, 20 events, 30-32
 * social, 40-44 streaks, 50-52 special, 60-65 Stories.
 */
export const SystemAchievementCriteriaType = {
  TotalAdventuresCompleted: 1,
  TotalAdventuresFailed: 2,
  TotalSoloAdventuresCompleted: 3,
  TotalGroupAdventuresCompleted: 4,
  UniqueAdventuresCompleted: 5,
  CategoryAdventuresCompleted: 6,
  TotalAdventuresCreated: 7,
  MaxUniquePlayersOnOwnedAdventure: 8,
  QualifyingAdventureCompletions: 9,
  TotalLocationsVisited: 10,
  TotalGeoCachesFound: 11,
  TotalDistanceKilometres: 12,
  TotalTrailKilometres: 13,
  TotalSteps: 14,
  TotalEventsAttended: 20,
  TotalFriendsMade: 30,
  TotalGroupSessionsLed: 31,
  TotalMessagesSent: 32,
  ConsecutiveDaysActive: 40,
  WeeklyCompletionStreak: 41,
  ConsecutiveWeeksActive: 42,
  ConsecutiveMonthsActive: 43,
  ConsecutiveYearsActive: 44,
  TopAdventureCompletion: 50,
  PerfectAdventureCompletion: 51,
  SpeedCompletion: 52,
  // Stories (Backlog 060): listed Stories only (public, active, approved by a moderator).
  StoriesStarted: 60,
  StoryChaptersCompleted: 61,
  StoriesFinished: 62,
  StoriesPublished: 63,
  PublishedStoryFinishers: 64,
  StoryRiddlesSolved: 65,
  Custom: 100,
} as const

export type SystemAchievementCriteriaTypeValue =
  (typeof SystemAchievementCriteriaType)[keyof typeof SystemAchievementCriteriaType]

/**
 * What each criterion is called, and what its threshold counts.
 *
 * The unit matters more than the name here. Distance is stored in metres and compared in
 * KILOMETRES, so a threshold of 100 means 100 km — someone typing 100000 expecting metres would
 * create a badge nobody can earn, and nothing would tell them.
 */
/**
 * What each criterion counts, and what an admin types into the threshold box.
 *
 * `unit` is the unit of the INPUT, not of storage. They differ for the two distance criteria: a
 * person setting a badge thinks in kilometres and would get "2500000" wrong, while the database
 * holds metres so that nothing has to divide, truncate, or guess when a reader in miles opens the
 * page. `storedIn` marks that gap, and `THRESHOLD_SCALE` is the factor between them.
 */
export const CRITERIA_LABELS: Record<
  number,
  { label: string; unit: string; storedIn?: string; note?: string }
> = {
  1: { label: 'Adventures completed', unit: 'completions' },
  2: { label: 'Adventures failed', unit: 'failures' },
  3: { label: 'Solo adventures completed', unit: 'completions' },
  4: { label: 'Group adventures completed', unit: 'completions' },
  5: { label: 'Unique adventures completed', unit: 'adventures' },
  6: {
    label: 'Adventures completed in a category',
    unit: 'completions',
    note: 'Needs a category id in the criteria config.',
  },
  7: { label: 'Adventures created', unit: 'adventures' },
  8: { label: 'Players on one owned adventure', unit: 'players' },
  9: { label: 'Adventures completed (repeat cooldown)', unit: 'adventures', note: 'Every distinct adventure counts. Repeating the same one pays nothing until the cooldown lapses — set it with {"cooldownDays":180} in the criteria config.' },
  10: { label: 'Locations visited', unit: 'locations' },
  11: { label: 'Geocaches found', unit: 'caches' },
  12: {
    label: 'Total distance travelled',
    unit: 'kilometres',
    storedIn: 'metres',
    note: 'Travel between completions (inferred from straight lines) plus recorded trails. Typed in kilometres, stored in metres.',
  },
  13: {
    label: 'Trail distance recorded',
    unit: 'kilometres',
    storedIn: 'metres',
    note: 'Recorded trail only — the drive between trailheads does not count. Typed in kilometres, stored in metres.',
  },
  14: {
    label: 'Steps',
    unit: 'steps',
    note: 'Counted only while a trail is being recorded, so most accounts sit at zero.',
  },
  20: { label: 'Events attended', unit: 'events' },
  30: { label: 'Friends made', unit: 'friends' },
  31: { label: 'Group sessions led', unit: 'sessions' },
  32: { label: 'Messages sent', unit: 'messages' },
  40: { label: 'Consecutive days active', unit: 'days' },
  41: {
    label: 'Weekly completion streak',
    unit: 'weeks',
    note: 'Nothing computes this one, so nobody makes progress on it. Use Consecutive weeks active instead.',
  },
  42: { label: 'Consecutive weeks active', unit: 'weeks', note: 'Best run of weeks (Monday start, in the user\'s time zone) with outdoor activity.' },
  43: { label: 'Consecutive months active', unit: 'months', note: 'Best run of calendar months with outdoor activity.' },
  44: { label: 'Consecutive years active', unit: 'years', note: 'Best run of calendar years with outdoor activity.' },
  50: { label: 'Top adventure completion', unit: 'completions' },
  51: { label: 'Perfect adventure completion', unit: 'completions' },
  52: { label: 'Speed completion', unit: 'completions' },
  60: {
    label: 'Stories started',
    unit: 'stories',
    note: 'A session on any stop of a listed Story (a chapter stop or its finale). Their own Stories never count.',
  },
  61: { label: 'Story chapters completed', unit: 'chapters', note: 'Chapters of listed Stories closed and still held.' },
  62: {
    label: 'Stories finished',
    unit: 'stories',
    note: 'Every chapter closed, plus the finale when the Story has one.',
  },
  63: { label: 'Stories published', unit: 'stories', note: 'Stories the user created or owns that a moderator approved and are public.' },
  64: {
    label: 'Players who finished one published Story',
    unit: 'players',
    note: 'The most players who finished any one Story the user published.',
  },
  65: { label: 'Story riddles solved', unit: 'riddles', note: 'Riddles solved on listed Stories the user did not write.' },
  100: { label: 'Custom', unit: 'units', note: 'Evaluated by whatever writes its progress.' },
}

/** Mirrors the server's `BadgeRarity`. Ordinals, as above. */
export const BADGE_RARITY_LABELS: Record<number, string> = {
  0: 'Common',
  1: 'Rare',
  2: 'Epic',
  3: 'Legendary',
}

export interface SystemAchievementSummaryDto {
  id: string
  name: string
  shortDescription: string
  iconUrl?: string | null
  criteriaType: number
  threshold: number
  points: number
  isActive: boolean
  isHidden: boolean
  displayOrder: number
}

export interface SystemAchievementDto extends SystemAchievementSummaryDto {
  fullDescription: string
  criteriaConfigJson?: string | null
  badgeImageId?: string | null
  badgeImageUrl?: string | null
  createdDate: string
  updatedDate: string
  createdByUserId: string
}

export interface CreateSystemAchievementRequest {
  name: string
  shortDescription: string
  fullDescription: string
  criteriaType: number
  threshold: number
  criteriaConfigJson?: string | null
  points: number
  isActive: boolean
  isHidden: boolean
  displayOrder: number
}

/** Every field optional: the server applies only what is sent. */
export type UpdateSystemAchievementRequest = Partial<CreateSystemAchievementRequest>

/**
 * Factor between what the admin types and what is stored, for criteria that differ.
 *
 * Applied in one place each way (the form's load and save) so the conversion cannot drift: divide
 * coming in, multiply going out. Everything not listed here is stored exactly as typed.
 */
export const THRESHOLD_SCALE: Record<number, number> = {
  12: 1000,
  13: 1000,
}

/** Stored value -> the number to show in the threshold box. */
export const toThresholdInput = (criteriaType: number, stored: number): number =>
  Math.round(stored / (THRESHOLD_SCALE[criteriaType] ?? 1))

/** Typed value -> the number to store. */
export const fromThresholdInput = (criteriaType: number, typed: number): number =>
  Math.round(typed * (THRESHOLD_SCALE[criteriaType] ?? 1))

/**
 * The `{threshold}` token, previewed the way an admin typed it — e.g. "100 km", "50 completions".
 *
 * Players see this substituted using their own metric/imperial setting (only meaningful for the
 * two distance criteria); there is no such reader here, so the preview always shows the admin's
 * own typed unit. It exists to show that the token resolves at all, not to match every reader.
 */
export const formatThresholdMeasurement = (criteriaType: number, typedThreshold: number): string => {
  const unit = CRITERIA_LABELS[criteriaType]?.unit ?? 'units'
  return `${typedThreshold.toLocaleString()} ${unit}`
}
